<template>
  <section class="config-group presets" aria-label="Presets">
    <span class="label">{{texts.presets.label}}:</span>
    <RadioGroup :items="presetGroups" name="config-preset" @input="onChange"/>
    <button class="undo" @click="undo" :title="texts.presets.undo + ' (ctrl + z)'" :disabled="!hasHistory ? 'disabled':undefined">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path d="M0 0h24v24H0z" fill="none"/>
        <path :fill="hasHistory ? '#4a4f62':'#efefef'" d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/>
      </svg>
    </button>
    <button class="restore" @click="onRestore">{{texts.presets.restoreDefaults}}</button>
  </section>
</template>

<script>
import Vue from "vue";
import RadioGroup from "../RadioGroup";

export default {
  name: "Presets",
  props: {
    config: {
      type: Object,
      required: true
    },
    afterChange: Function,
    onRestore: Function,
    onUndo: Function
  },
  components: { RadioGroup },
  data() {
    return {
      activePreset: "",
      hasHistory: false
    };
  },
  computed: {
    presetGroups() {
      return [
        {
          id: "preset-minimal--i",
          label: this.texts.presets.minimal,
          value: "minimal",
          isActive: this.activePreset == "minimal"
        },
        {
          id: "preset-advanced--i",
          label: this.texts.presets.advanced,
          value: "advanced",
          isActive: this.activePreset == "advanced"
        },
        {
          id: "preset-flat--i",
          label: this.texts.presets.flat,
          value: "flat",
          isActive: this.activePreset == "flat"
        },
        {
          id: "preset-rounded--i",
          label: this.texts.presets.rounded,
          value: "rounded",
          isActive: this.activePreset == "rounded"
        }
      ];
    }
  },
  mounted() {
    this.updateHasHistory();
  },
  watch: {
    config: {
      deep: true,
      handler() {
        this.updateHasHistory();
      }
    }
  },

  methods: {
    undo() {
      this.activePreset = "";
      this.onUndo();
    },
    updateHasHistory() {
      clearTimeout(this.historyUpdateTimeout);
      this.historyUpdateTimeout = setTimeout(
        function() {
          this.hasHistory =
            JSON.parse(
              window.localStorage.getItem("ss360Config-history") || "[]"
            ).length > 1;
        }.bind(this),
        100
      );
    },
    initConfigs() {
      const config = this.config;
      if (config.style === undefined) {
        Vue.set(config, "style", {});
      }
      if (config.style.searchBox === undefined) {
        Vue.set(config.style, "searchBox", {});
      }
      if (config.style.suggestions === undefined) {
        Vue.set(config.style, "suggestions", {});
      }
      if (config.layout === undefined) {
        Vue.set(config, "layout", {});
      }
      if (config.layout.navigation === undefined) {
        Vue.set(config.layout, "navigation", {});
      }
      if (config.layout.mobile === undefined) {
        Vue.set(config.layout, "mobile", {});
      }
      if (config.layout.desktop === undefined) {
        Vue.set(config.layout, "desktop", {});
      }
      if (config.suggestions === undefined) {
        Vue.set(config, "suggestions", {});
      }
    },

    onChange(activeItem) {
      if (activeItem === "minimal") {
        this.setMinimal();
      } else if (activeItem == "advanced") {
        this.setAdvanced();
      } else if (activeItem == "flat") {
        this.setFlat();
      } else if (activeItem == "rounded") {
        this.setRounded();
      }
      if ("scrollBy" in window) {
        const boundingRect = document
          .getElementById("playground-sm")
          .getBoundingClientRect();
        if (boundingRect.x !== 0 && boundingRect.y !== 0) {
          window.scrollBy({
            top: boundingRect.top,
            left: 0,
            behavior: "smooth"
          });
        }
      } else {
        window.location.hash = "playground-sm";
      }
      if (this.afterChange !== undefined) {
        this.afterChange(activeItem);
      }
    },

    setMinimal() {
      this.activePreset = "minimal";
      this.initConfigs();
      const config = this.config;

      Vue.set(config.layout.navigation, "position", "none");
      Vue.set(config.layout.mobile, "showImages", false);
      Vue.set(config.layout.mobile, "showUrl", false);
      Vue.set(config.layout.desktop, "showImages", false);
      Vue.set(config.layout.desktop, "showUrl", false);
      Vue.set(config.suggestions, "show", false);
      Vue.set(config.suggestions, "showImages", false);

      Vue.set(config.style.searchBox, "text", {
        color: "#333333",
        size: "12px"
      });
      Vue.set(config.style.searchBox, "background", { color: "#ffffff" });
      Vue.set(config.style.searchBox, "padding", "8px");
      Vue.set(config.style.searchBox, "border", {
        color: "#dddddd",
        radius: "0px"
      });
      Vue.set(config.style.searchBox, "icon", undefined);
      Vue.set(config.style.searchBox, "button", undefined);
      Vue.set(config.searchBox, "searchButton", undefined);

      Vue.set(config.style.suggestions, "text", { color: "#333333" });
      Vue.set(config.style.suggestions, "background", { color: "#ffffff" });
      Vue.set(config.style.suggestions, "padding", "10px");
      Vue.set(config.style.suggestions, "distanceFromTop", "0px");
      Vue.set(config.style.suggestions, "border", {
        color: "#dddddd",
        radius: "0px"
      });

      Vue.set(config.style, "themeColor", undefined);
      Vue.set(config.style, "loaderType", undefined);
    },

    setAdvanced() {
      this.activePreset = "advanced";
      this.initConfigs();
      const config = this.config;

      Vue.set(config.layout.navigation, "position", "top");

      Vue.set(config.layout.mobile, "showImages", true);
      Vue.set(config.layout.mobile, "showUrl", true);
      Vue.set(config.layout.desktop, "showImages", true);
      Vue.set(config.layout.desktop, "showUrl", true);
      Vue.set(config.suggestions, "show", true);
      Vue.set(config.suggestions, "showImages", true);

      Vue.set(config.style.searchBox, "text", {
        color: "#333333",
        size: "14px"
      });
      Vue.set(config.style.searchBox, "background", { color: "#ffffff" });
      Vue.set(config.style.searchBox, "padding", "10px");
      Vue.set(config.style.searchBox, "border", {
        color: "#dddddd",
        radius: "0px"
      });
      Vue.set(config.style.searchBox, "icon", {
        image: "magnifier",
        color: "#666666"
      });
      Vue.set(config.style.searchBox, "button", undefined);
      Vue.set(config.searchBox, "searchButton", undefined);

      Vue.set(config.style.suggestions, "text", { color: "#333333" });
      Vue.set(config.style.suggestions, "background", { color: "#ffffff" });
      Vue.set(config.style.suggestions, "padding", "5px");
      Vue.set(config.style.suggestions, "distanceFromTop", "0px");
      Vue.set(config.style.suggestions, "border", {
        color: "#dddddd",
        radius: "0px"
      });

      Vue.set(config.style, "themeColor", undefined);
      Vue.set(config.style, "loaderType", undefined);
    },

    setFlat() {
      this.activePreset = "flat";

      this.initConfigs();
      const config = this.config;

      Vue.set(config.layout.navigation, "position", "top");

      Vue.set(config.layout.mobile, "showImages", true);
      Vue.set(config.layout.mobile, "showUrl", true);
      Vue.set(config.layout.desktop, "showImages", true);
      Vue.set(config.layout.desktop, "showUrl", true);
      Vue.set(config.suggestions, "show", true);
      Vue.set(config.suggestions, "showImages", true);

      Vue.set(config.style.searchBox, "text", {
        color: "#1a74d7",
        size: "14px"
      });
      Vue.set(config.style.searchBox, "background", { color: "#f0f0f0" });
      Vue.set(config.style.searchBox, "padding", "10px");
      Vue.set(config.style.searchBox, "border", {
        color: "#ffffff",
        radius: "0px"
      });
      Vue.set(config.style.searchBox, "button", {
        text: " ",
        icon: "magnifier",
        color: "#666666"
      });
      Vue.set(config.searchBox, "searchButton", "#searchButton");
      Vue.set(config.style.searchBox, "icon", undefined);

      Vue.set(config.style.suggestions, "text", { color: "#1a74d7" });
      Vue.set(config.style.suggestions, "background", { color: "#f0f0f0" });
      Vue.set(config.style.suggestions, "padding", "10px");
      Vue.set(config.style.suggestions, "distanceFromTop", "5px");
      Vue.set(config.style.suggestions, "border", {
        color: "#ffffff",
        radius: "0px"
      });

      Vue.set(config.style, "themeColor", "#1a74d7");
      Vue.set(config.style, "loaderType", "square");
    },

    setRounded() {
      this.activePreset = "rounded";

      this.initConfigs();
      const config = this.config;

      Vue.set(config.layout.navigation, "position", "top");

      Vue.set(config.layout.mobile, "showImages", true);
      Vue.set(config.layout.mobile, "showUrl", true);
      Vue.set(config.layout.desktop, "showImages", true);
      Vue.set(config.layout.desktop, "showUrl", true);
      Vue.set(config.suggestions, "show", true);
      Vue.set(config.suggestions, "showImages", true);

      Vue.set(config.style.searchBox, "text", {
        color: "#333333",
        size: "14px"
      });
      Vue.set(config.style.searchBox, "background", { color: "#ffffff" });
      Vue.set(config.style.searchBox, "padding", "10px");
      Vue.set(config.style.searchBox, "border", {
        color: "#dddddd",
        radius: "7px"
      });
      Vue.set(config.style.searchBox, "button", {
        text: "Search",
        icon: undefined,
        color: "#666666"
      });
      Vue.set(config.searchBox, "searchButton", "#searchButton");
      Vue.set(config.style.searchBox, "icon", {
        image: "magnifier",
        color: "#666666"
      });

      Vue.set(config.style.suggestions, "text", { color: "#333333" });
      Vue.set(config.style.suggestions, "background", { color: "#ffffff" });
      Vue.set(config.style.suggestions, "padding", "10px");
      Vue.set(config.style.suggestions, "distanceFromTop", "7px");
      Vue.set(config.style.suggestions, "border", {
        color: "#dddddd",
        radius: "7px"
      });

      Vue.set(config.style, "themeColor", undefined);
      Vue.set(config.style, "loaderType", undefined);
    }
  }
};
</script>

<style>
.presets button.restore {
  background: none;
  color: #c64624;
  position: absolute;
  right: 0px;
  border: 1px solid #c64624;
  cursor: pointer;
  border-radius: 3px;
  box-shadow: 2px 2px 4px 0 rgba(0, 0, 0, 0.12);
  font-weight: 500;
  padding: 3px 20px;
  line-height: 24px;
  top: -20px;
  transition: all 0.3s ease-in-out;
}

.presets button.restore:hover,
.presets button.restore.active {
  background: #c64624;
  border-color: #c64624;
  color: #fff;
}

.presets button.undo {
  position: absolute;
  left: -3px;
  top: -14px;
  margin: 0;
  border: none;
  background: #fff;
  padding: 3px;
  transition: all .3s ease-in-out;
}

.presets button.undo:not(:disabled):hover {
  cursor: pointer;
  background: #efefef;
}

.config-group.presets .label {
  font-weight: 500;
  margin-right: 0.5em;
}
</style>



// WEBPACK FOOTER //
// src/components/configuration/Presets.vue